import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import './SimpleDashboard.css';

const PERIODS = [
  { id: 'hoy', label: 'Hoy' },
  { id: '7dias', label: '7 días' },
  { id: '30dias', label: '30 días' },
  { id: 'mes', label: 'Este mes' }
];

const ORDER_STATUS_PIPELINE = [
  ['pending', 'Pendiente'],
  ['awaiting_packaging', 'Empaque pendiente'],
  ['ready_to_ship', 'Cola empaque (legacy)'],
  ['processing', 'Listo para guía'],
  ['shipped', 'Enviado'],
  ['delivered', 'Entregado'],
  ['cancelled', 'Cancelado']
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);

const SimpleDashboard = ({ operationsSummary = null, onNavigate }) => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('7dias');

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminService.getKpis(period);
        setKpis(data);
      } catch (err) {
        console.error('Error cargando KPIs:', err);
        setError(
          err.response?.status === 401
            ? 'Sesión expirada. Inicia sesión de nuevo.'
            : 'Error al cargar los datos del dashboard.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchKpis();
  }, [period]);

  const go = (section, queue = '') => {
    if (typeof onNavigate === 'function') {
      onNavigate(section, queue);
    }
  };

  if (loading && !kpis) {
    return (
      <div className="simple-dashboard">
        <div className="dashboard-header">
          <h1>Centro de mando</h1>
          <p>Cargando datos en vivo...</p>
        </div>
        <div className="dashboard-loading">
          <i className="fas fa-spinner fa-spin fa-3x" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="simple-dashboard">
        <div className="dashboard-header">
          <h1>Centro de mando</h1>
        </div>
        <div className="dashboard-error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const { ventas, pedidos, ticket_promedio, productos_activos, ordenes_pendientes, comparativa } = kpis || {};
  const ventasVar = comparativa?.ventas_variacion ?? 0;
  const pedidosVar = comparativa?.pedidos_variacion ?? 0;

  const byStatus = operationsSummary?.orders?.by_status || {};
  const queues = operationsSummary?.orders?.queues || {};
  const payOpen = operationsSummary?.orders?.payment_pending_open;
  const pendingInvoices = operationsSummary?.invoices_pending_review ?? operationsSummary?.badges?.invoices;
  const pendingTestimonials =
    operationsSummary?.testimonials_pending ?? operationsSummary?.badges?.testimonials;
  const dropshippingCount = queues.dropshipping ?? operationsSummary?.dropshipping_pending ?? 0;
  const packagingChia = queues.packaging_javiland ?? queues.packaging ?? 0;

  const actionCards = [
    {
      id: 'dropshipping',
      label: 'Dropshipping pendiente',
      count: dropshippingCount,
      hint: 'Pedidos pagados → WhatsApp Himalaya',
      section: 'orders',
      queue: 'dropshipping',
      icon: 'fa-truck-fast'
    },
    {
      id: 'packaging',
      label: 'Empaque Chía',
      count: packagingChia,
      hint: 'Stock propio · CHK-001',
      section: 'packaging',
      queue: '',
      icon: 'fa-box-open'
    },
    {
      id: 'shipping',
      label: 'Listos para guía',
      count: queues.shipping_need_guide ?? 0,
      hint: 'Generar tracking Envia',
      section: 'orders',
      queue: 'shipping_guide',
      icon: 'fa-shipping-fast'
    },
    {
      id: 'payments',
      label: 'Pagos abiertos',
      count: payOpen ?? 0,
      hint: 'Pending / failed sin cancelar',
      section: 'orders',
      queue: 'payment_pending',
      icon: 'fa-credit-card'
    },
    {
      id: 'invoices',
      label: 'Facturas en revisión',
      count: pendingInvoices ?? 0,
      hint: 'Numbers / aprobación',
      section: 'invoices',
      queue: '',
      icon: 'fa-file-invoice'
    },
    {
      id: 'testimonials',
      label: 'Testimonios',
      count: pendingTestimonials ?? 0,
      hint: 'Moderación web',
      section: 'testimonios',
      queue: '',
      icon: 'fa-comments'
    }
  ];

  return (
    <div className="simple-dashboard">
      <div className="dashboard-header">
        <h1>Centro de mando</h1>
        <p>Qué hacer hoy — datos en vivo desde la base de datos</p>
        <div className="dashboard-period-block">
          <span className="dashboard-period-label">Período de ventas</span>
          <div className="period-selector period-selector--prominent" role="tablist" aria-label="Período de ventas">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={period === p.id}
                className={`period-btn ${period === p.id ? 'active' : ''}`}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {operationsSummary && (
          <section className="dashboard-ops-section" aria-label="Bandejas operativas">
            <h2 className="dashboard-ops-title">Mi día — bandejas de trabajo</h2>
            <p className="dashboard-ops-intro">
              Haz clic en una bandeja para ir directo a la cola. Los conteos coinciden con los badges del menú lateral.
            </p>
            <div className="dashboard-action-grid">
              {actionCards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  className={`dashboard-action-card${card.count > 0 ? ' has-work' : ''}`}
                  onClick={() => go(card.section, card.queue)}
                >
                  <i className={`fas ${card.icon}`} aria-hidden />
                  <div className="dashboard-action-card-body">
                    <span className="dashboard-action-card-label">{card.label}</span>
                    <strong className="dashboard-action-card-count">{card.count}</strong>
                    <span className="dashboard-action-card-hint">{card.hint}</span>
                  </div>
                  <i className="fas fa-chevron-right dashboard-action-card-arrow" aria-hidden />
                </button>
              ))}
            </div>

            <div className="dashboard-ops-table-wrap">
              <table className="dashboard-ops-table">
                <caption className="visually-hidden">Cantidad de órdenes por estado en base de datos</caption>
                <thead>
                  <tr>
                    <th scope="col">Estado (BD)</th>
                    <th scope="col">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {ORDER_STATUS_PIPELINE.map(([key, label]) => (
                    <tr key={key}>
                      <td>
                        <code className="dashboard-ops-code">{key}</code>
                        <span className="dashboard-ops-label"> {label}</span>
                      </td>
                      <td>{byStatus[key] ?? 0}</td>
                    </tr>
                  ))}
                  {Object.keys(byStatus)
                    .filter((k) => !ORDER_STATUS_PIPELINE.some(([key]) => key === k))
                    .map((k) => (
                      <tr key={`extra-${k}`}>
                        <td>
                          <code className="dashboard-ops-code">{k}</code>
                        </td>
                        <td>{byStatus[k]}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <h2 className="dashboard-kpi-heading">Ventas y catálogo</h2>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-icon sales">
              <i className="fas fa-dollar-sign" />
            </div>
            <div className="card-content">
              <h3>Ventas</h3>
              <p className="card-number">{formatCurrency(ventas)}</p>
              <p className={`card-change ${ventasVar >= 0 ? 'positive' : 'negative'}`}>
                {ventasVar >= 0 ? '+' : ''}
                {ventasVar}% vs período anterior
              </p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon orders">
              <i className="fas fa-shopping-cart" />
            </div>
            <div className="card-content">
              <h3>Pedidos</h3>
              <p className="card-number">{pedidos ?? 0}</p>
              <p className={`card-change ${pedidosVar >= 0 ? 'positive' : 'negative'}`}>
                {pedidosVar >= 0 ? '+' : ''}
                {pedidosVar}% vs período anterior
              </p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon ticket">
              <i className="fas fa-receipt" />
            </div>
            <div className="card-content">
              <h3>Ticket promedio</h3>
              <p className="card-number">{formatCurrency(ticket_promedio)}</p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon products">
              <i className="fas fa-box" />
            </div>
            <div className="card-content">
              <h3>Productos activos</h3>
              <p className="card-number">{productos_activos ?? 0}</p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon pending">
              <i className="fas fa-truck" />
            </div>
            <div className="card-content">
              <h3>Órdenes pendientes</h3>
              <p className="card-number">{ordenes_pendientes ?? 0}</p>
              <p className="card-subtitle">Por enviar</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDashboard;
